import { Injectable, inject } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { LoggerService } from './logger.service';

export type CalculationKind =
  | 'date_diff_between_fields'
  | 'date_diff_from_previous'
  | 'arithmetic_two_fields'
  | 'checklist_total_metric'
  | 'state_transition';

export type ArithmeticOperator = 'add' | 'subtract' | 'multiply' | 'divide';
export type EmptyBehavior = 'null' | 'zero';

export const CALCULATED_METADATA_SOURCES = {
  previousSubmissionTime: '__meta.previous_submission_time',
  now: '__meta.now',
} as const;

export interface CalculatedEvaluationContext {
  currentSubmissionCreatedAt?: string;
  previousSubmissionCreatedAt?: string;
  nowIsoString?: string;
}

export interface CalculatedFieldProps {
  calculationKind?: CalculationKind;
  inputFieldAId?: string;
  inputFieldBId?: string;
  currentFieldId?: string;
  checklistFieldId?: string;
  arithmeticOperator?: ArithmeticOperator;
  emptyBehavior?: EmptyBehavior;
  persistSnapshot?: true;
  sourceFieldId?: string;
  outputFieldId?: string;
  transitions?: Array<{ from?: string[] | string; to?: string }>;
  transitionFallback?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CalculatedFieldEvaluatorService {
  private logger = inject(LoggerService).getLogger('calculated-field-evaluator');

  evaluateCalculatedFields(
    formModel: Record<string, unknown>,
    formFields: FormlyFieldConfig[],
    previousSubmissionFormData?: Record<string, unknown> | null,
    context?: CalculatedEvaluationContext,
  ): Record<string, string | number | null> {
    const results: Record<string, string | number | null> = {};

    for (const field of formFields) {
      if (field.type !== 'calculated' || typeof field.key !== 'string') {
        continue;
      }

      const props = (field.props ?? {}) as CalculatedFieldProps;
      const value = this.evaluateSingleCalculatedField(
        props,
        formModel,
        formFields,
        previousSubmissionFormData ?? undefined,
        context,
      );

      results[field.key] = value;
    }

    return results;
  }

  private evaluateSingleCalculatedField(
    props: CalculatedFieldProps,
    formModel: Record<string, unknown>,
    formFields: FormlyFieldConfig[],
    previousSubmissionFormData?: Record<string, unknown>,
    context?: CalculatedEvaluationContext,
  ): string | number | null {
    let rawValue: string | number | null;

    switch (props.calculationKind) {
      case 'date_diff_between_fields':
        rawValue = this.dateDiffBetweenFields(
          props.inputFieldAId,
          props.inputFieldBId,
          formModel,
          previousSubmissionFormData,
          context,
          props,
        );
        break;
      case 'date_diff_from_previous':
        rawValue = this.dateDiffFromPrevious(formModel, previousSubmissionFormData, context, props);
        break;
      case 'arithmetic_two_fields':
        rawValue = this.arithmeticTwoFields(
          formModel[props.inputFieldAId ?? ''],
          formModel[props.inputFieldBId ?? ''],
          props,
        );
        break;
      case 'checklist_total_metric':
        rawValue = this.checklistTotalMetric(formModel, formFields, props);
        break;
      case 'state_transition':
        rawValue = this.stateTransition(previousSubmissionFormData, props);
        break;
      default:
        this.logger.warn('Unknown calculation kind encountered', props.calculationKind);
        rawValue = this.emptyFallback(props.emptyBehavior);
        break;
    }

    if (rawValue === null) {
      return null;
    }

    // Calculated values are persisted as canonical raw numbers.
    return rawValue;
  }

  private stateTransition(
    previousSubmissionFormData: Record<string, unknown> | undefined,
    props: CalculatedFieldProps,
  ): string | null {
    const sourceFieldId = props.sourceFieldId;
    if (!sourceFieldId) {
      return props.transitionFallback ?? null;
    }

    const previousRaw = previousSubmissionFormData?.[sourceFieldId];
    const previousValues = this.normalizeStateValues(previousRaw);
    const transitions = Array.isArray(props.transitions) ? props.transitions : [];

    for (const transition of transitions) {
      const fromValues = this.normalizeTransitionFromValues(transition?.from);
      // Multi-value sources (checkbox-group / multi-select) match when ANY of the
      // currently-selected values is listed in the rule's `from`.
      if (fromValues.some((fromValue) => previousValues.includes(fromValue))) {
        const nextValue = String(transition?.to ?? '').trim();
        return nextValue === '' ? null : nextValue;
      }
    }

    return props.transitionFallback ?? null;
  }

  private normalizeStateValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    return String(value);
  }

  /**
   * Normalizes a source field's value into the list of selected state strings.
   * Single-value fields yield a one-element array; multi-value fields yield one
   * entry per selection. An empty/absent value yields `['']` so that a
   * `from: ['']` rule still matches "no previous state" (e.g. first submission).
   */
  private normalizeStateValues(value: unknown): string[] {
    if (Array.isArray(value)) {
      const normalized = value
        .map((entry) => this.normalizeStateValue(entry))
        .filter((entry) => entry !== '');
      return normalized.length > 0 ? normalized : [''];
    }

    return [this.normalizeStateValue(value)];
  }

  private normalizeTransitionFromValues(from: string[] | string | undefined): string[] {
    if (Array.isArray(from)) {
      return from.map((value) => this.normalizeStateValue(value));
    }

    if (typeof from === 'string') {
      return from.split(',').map((value) => value.trim());
    }

    return [];
  }

  private dateDiffBetweenFields(
    startSourceId: string | undefined,
    endSourceId: string | undefined,
    formModel: Record<string, unknown>,
    previousSubmissionFormData: Record<string, unknown> | undefined,
    context: CalculatedEvaluationContext | undefined,
    props: CalculatedFieldProps,
  ): number | null {
    const startValue = this.resolveDateSourceValue(
      startSourceId,
      'current',
      formModel,
      previousSubmissionFormData,
      context,
    );
    const endValue = this.resolveDateSourceValue(
      endSourceId,
      'current',
      formModel,
      previousSubmissionFormData,
      context,
    );

    const start = this.parseDate(startValue);
    const end = this.parseDate(endValue);

    this.logger.debug('date_diff_between_fields resolved sources', {
      startSourceId,
      endSourceId,
      startValue,
      endValue,
    });

    if (!start || !end) {
      return this.emptyFallback(props.emptyBehavior);
    }

    // Field A is treated as start and Field B as end.
    const diffMs = end.getTime() - start.getTime();
    return diffMs / 1000;
  }

  private dateDiffFromPrevious(
    formModel: Record<string, unknown>,
    previousSubmissionFormData: Record<string, unknown> | undefined,
    context: CalculatedEvaluationContext | undefined,
    props: CalculatedFieldProps,
  ): number | null {
    const sourceId = props.currentFieldId;
    const current = this.parseDate(
      this.resolveDateSourceValue(
        sourceId,
        'current',
        formModel,
        previousSubmissionFormData,
        context,
      ),
    );
    const previous = this.parseDate(
      this.resolveDateSourceValue(
        sourceId,
        'previous',
        formModel,
        previousSubmissionFormData,
        context,
      ),
    );

    if (!current || !previous) {
      return this.emptyFallback(props.emptyBehavior);
    }

    const diffMs = current.getTime() - previous.getTime();
    return diffMs / 1000;
  }

  private arithmeticTwoFields(
    valueA: unknown,
    valueB: unknown,
    props: CalculatedFieldProps,
  ): number | null {
    const a = this.parseNumber(valueA);
    const b = this.parseNumber(valueB);

    if (a === null || b === null) {
      return this.emptyFallback(props.emptyBehavior);
    }

    const operator = props.arithmeticOperator ?? 'add';
    if (operator === 'add') {
      return a + b;
    }
    if (operator === 'subtract') {
      return a - b;
    }
    if (operator === 'multiply') {
      return a * b;
    }

    if (b === 0) {
      return this.emptyFallback(props.emptyBehavior);
    }

    return a / b;
  }

  private checklistTotalMetric(
    formModel: Record<string, unknown>,
    formFields: FormlyFieldConfig[],
    props: CalculatedFieldProps,
  ): number | null {
    const checklistFieldId = props.checklistFieldId ?? '';
    const selectedRaw = formModel[checklistFieldId];

    if (
      !Array.isArray(selectedRaw) &&
      selectedRaw !== null &&
      selectedRaw !== undefined &&
      selectedRaw !== ''
    ) {
      return this.emptyFallback(props.emptyBehavior);
    }

    const selectedValues: string[] = Array.isArray(selectedRaw)
      ? selectedRaw.map((v) => String(v))
      : selectedRaw
        ? [String(selectedRaw)]
        : [];

    if (selectedValues.length === 0) {
      return this.emptyFallback(props.emptyBehavior);
    }

    const sourceField = formFields.find((f) => String(f.key ?? '') === checklistFieldId);
    const options = Array.isArray(sourceField?.props?.['options'])
      ? (sourceField?.props?.['options'] as Array<Record<string, unknown>>)
      : [];

    const metricByOption = new Map<string, number>();
    for (const option of options) {
      const value = String(option['value'] ?? '');
      if (!value) {
        continue;
      }
      const metric = this.parseNumber(option['metricAmount']);
      metricByOption.set(value, metric ?? 0);
    }

    return selectedValues.reduce(
      (sum, selectedId) => sum + (metricByOption.get(selectedId) ?? 0),
      0,
    );
  }

  private parseDate(value: unknown): Date | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    return null;
  }

  private resolveDateSourceValue(
    sourceId: string | undefined,
    scope: 'current' | 'previous',
    formModel: Record<string, unknown>,
    previousSubmissionFormData: Record<string, unknown> | undefined,
    context: CalculatedEvaluationContext | undefined,
  ): unknown {
    if (!sourceId) {
      return undefined;
    }

    if (sourceId === CALCULATED_METADATA_SOURCES.previousSubmissionTime) {
      return context?.previousSubmissionCreatedAt;
    }
    if (sourceId === CALCULATED_METADATA_SOURCES.now) {
      return scope === 'current'
        ? (context?.nowIsoString ?? context?.currentSubmissionCreatedAt)
        : context?.previousSubmissionCreatedAt;
    }

    return scope === 'current' ? formModel[sourceId] : previousSubmissionFormData?.[sourceId];
  }

  private parseNumber(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private emptyFallback(emptyBehavior: EmptyBehavior | undefined): number | null {
    return emptyBehavior === 'zero' ? 0 : null;
  }
}
